import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eyebrow } from './UI';
import { Crosshair, Target, Users, Ghost, Video, Trophy, ArrowUpRight } from 'lucide-react';
export const UseCases = () => {
  const cases = [{
    num: '01',
    Icon: Crosshair,
    title: 'AIRSOFT WARFARE',
    tag: 'BALLISTIC PROTECTION',
    body: "Full-face BB protection at up to 380 FPS. Anti-fog polycarbonate lenses rated ANSI Z87.1. Maximum peripheral vision. Zero blind spots. The field will know you're serious.",
    videoSrc: 'https://cdn.magicpatterns.com/uploads/soNRBxuFmUQtqQHyKwkyRa/hf_20260513_143208_48f292b4-b3e8-4b62-9051-d6b3a410fa4e.mp4'
  }, {
    num: '02',
    Icon: Target,
    title: 'TACTICAL TRAINING',
    tag: 'FORCE-ON-FORCE',
    body: 'Force-on-force simulation. Live-fire range protection. Low-profile cheek clearance designed for a proper stock weld. Built to work with your training — not against it.',
    videoSrc: 'https://cdn.magicpatterns.com/uploads/uiZB8yWemuKHxxi4XU21Qy/hf_20260506_145717_f97e664f-a509-4158-bb00-16e1ceb50cdc.mp4'
  }, {
    num: '03',
    Icon: Users,
    title: 'COSPLAY & CONVENTIONS',
    tag: 'ALL-DAY WEAR',
    body: 'Six interchangeable lens colors. All-day wearability. Adjustable ballistic nylon strap fits every head. Walk the floor. Win every room. No competition.',
    videoSrc: 'https://cdn.magicpatterns.com/uploads/hCMgw1VwAQHKDdARCcUpEt/Video Project.mp4'
  }, {
    num: '04',
    Icon: Ghost,
    title: 'EVENTS & HALLOWEEN',
    tag: 'INDESTRUCTIBLE',
    body: 'The only event mask that can survive being run over. While other costumes fall apart by 10pm — yours still looks like it came off a film set.',
    videoSrc: 'https://cdn.magicpatterns.com/uploads/pBQYjrFrdAm7d6jUJBXDfR/hf_20260513_143108_c8bc823e-846b-4da6-9ee7-045827c1f991.mp4'
  }, {
    num: '05',
    Icon: Video,
    title: 'STREAMING & CONTENT',
    tag: 'ON-CAMERA PRESENCE',
    body: "Command your audience's attention instantly. The mirrored lenses hide your eyes while catching ring lights perfectly, creating an intimidating, anonymous persona that keeps viewers locked in. No glare, no fog, just pure aesthetic dominance on stream.",
    videoSrc: 'https://cdn.magicpatterns.com/uploads/2NaWVthi5jQ7uTzZ8aa2sP/hf_20260513_155130_e12c5ae2-534d-4bfc-8687-94f15e654536.mp4'
  }, {
    num: '06',
    Icon: Trophy,
    title: 'COMPETITION & SPORT',
    tag: 'SQUAD GRADE',
    body: 'Paintball. Laser tag. Battle simulation. When your team looks like a unit — they fight like one. The BLACKOUT UNIT Ultimate Mask makes the whole squad elite.'
  }];
  const [active, setActive] = useState(0);
  const current = cases[active];
  return <section className="bg-[#0a0a0a] pt-[60px] pb-[40px] overflow-hidden" data-id="element-693">
      <div className="container mx-auto px-6 md:px-12" data-id="element-694">
        <div className="text-center mb-14" data-id="element-695">
          <Eyebrow data-id="element-696">MISSION VERSATILITY</Eyebrow>
          <h2 className="text-5xl md:text-[72px] leading-[0.9] mb-4" data-id="element-697">
            ONE MASK. EVERY THEATER.
          </h2>
          <p className="font-barlow text-lg text-text-body" data-id="element-698">
            We designed it once. It deploys everywhere.
          </p>
        </div>

        {/* Interactive Selector + Display */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-stretch" data-id="element-699">
          {/* Left: Numbered Selector */}
          <div className="lg:col-span-5" data-id="element-700">
            <div className="flex flex-col" data-id="element-701">
              {cases.map((c, i) => {
              const isActive = active === i;
              return <button key={i} onClick={() => setActive(i)} onMouseEnter={() => setActive(i)} className={`group relative flex items-center gap-5 py-5 border-b border-dark-3 text-left transition-colors duration-300 ${isActive ? 'text-white' : 'text-text-dimmed hover:text-white'}`} data-id="element-702">
                    {/* Animated active bar */}
                    {isActive && <motion.span layoutId="useCaseBar" className="absolute left-0 top-0 bottom-0 w-[2px] bg-acid-green" transition={{
                  type: 'spring',
                  stiffness: 400,
                  damping: 30
                }} data-id="element-703" />}

                    <span className={`font-bebas text-2xl md:text-3xl tracking-wider w-12 transition-colors duration-300 ${isActive ? 'text-acid-green' : 'text-text-dimmed/60'}`} data-id="element-704">
                      {c.num}
                    </span>

                    <div className="flex items-center gap-3 flex-1" data-id="element-705">
                      <c.Icon size={18} strokeWidth={2} className={`shrink-0 transition-colors duration-300 ${isActive ? 'text-acid-green' : 'text-text-dimmed/60'}`} data-id="element-706" />
                      <span className="font-bebas text-xl md:text-2xl tracking-wide" data-id="element-707">
                        {c.title}
                      </span>
                    </div>

                    <ArrowUpRight size={16} className={`shrink-0 transition-all duration-300 ${isActive ? 'text-acid-green translate-x-0 opacity-100' : '-translate-x-2 opacity-0 group-hover:opacity-50 group-hover:translate-x-0'}`} data-id="element-708" />
                  </button>;
            })}
            </div>
          </div>

          {/* Right: Active Display Panel */}
          <div className="lg:col-span-7" data-id="element-709">
            <div className="relative h-full min-h-[420px] bg-base-black border border-dark-3 overflow-hidden" data-id="element-710">
              {/* Cinematic Video Background */}
              <div className="absolute inset-0 z-0" data-id="element-711">
                <AnimatePresence mode="wait" data-id="element-712">
                  <motion.video key={current.videoSrc || 'default-video'} initial={{
                  opacity: 0
                }} animate={{
                  opacity: 0.8
                }} exit={{
                  opacity: 0
                }} transition={{
                  duration: 0.5
                }} autoPlay loop muted playsInline className="absolute inset-0 w-full h-full object-cover" data-id="element-713">
                    <source src={current.videoSrc || 'https://cdn.magicpatterns.com/uploads/hoqbokUhsfYRknubxk99SE/hf_20260513_155658_06e7bfdc-782a-4874-be5d-bd4bb277010e.mp4'} type="video/mp4" data-id="element-714" />
                  </motion.video>
                </AnimatePresence>
                {/* Dark overlay for text readability */}
                <div className="absolute inset-0 bg-gradient-to-r from-base-black/60 via-base-black/30 to-transparent" data-id="element-715"></div>
              </div>

              {/* Tactical grid overlay */}
              <div className="absolute inset-0 bg-tactical-grid bg-tactical-grid opacity-30 pointer-events-none z-0" data-id="element-716"></div>

              {/* Massive number watermark */}
              <AnimatePresence mode="wait" data-id="element-717">
                <motion.span key={`num-${active}`} initial={{
                opacity: 0,
                scale: 1.1
              }} animate={{
                opacity: 0.06,
                scale: 1
              }} exit={{
                opacity: 0
              }} transition={{
                duration: 0.4
              }} className="absolute -right-8 -bottom-16 font-bebas text-[260px] md:text-[360px] leading-none text-acid-green pointer-events-none select-none" data-id="element-718">
                  {current.num}
                </motion.span>
              </AnimatePresence>

              {/* Corner brackets */}
              <div className="absolute top-0 left-0 w-10 h-10 border-t-2 border-l-2 border-acid-green/40 pointer-events-none" data-id="element-719"></div>
              <div className="absolute top-0 right-0 w-10 h-10 border-t-2 border-r-2 border-acid-green/40 pointer-events-none" data-id="element-720"></div>
              <div className="absolute bottom-0 left-0 w-10 h-10 border-b-2 border-l-2 border-acid-green/40 pointer-events-none" data-id="element-721"></div>
              <div className="absolute bottom-0 right-0 w-10 h-10 border-b-2 border-r-2 border-acid-green/40 pointer-events-none" data-id="element-722"></div>

              {/* Content */}
              <div className="relative z-10 p-8 md:p-12 h-full flex flex-col" data-id="element-723">
                <AnimatePresence mode="wait" data-id="element-724">
                  <motion.div key={`content-${active}`} initial={{
                  opacity: 0,
                  y: 20
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -20
                }} transition={{
                  duration: 0.35,
                  ease: 'easeOut'
                }} className="flex-1 flex flex-col" data-id="element-725">
                    {/* Tag */}
                    <div className="inline-flex items-center gap-2 self-start border border-acid-green/30 bg-acid-green/5 px-2.5 py-1 mb-6" data-id="element-726">
                      <span className="w-1 h-1 bg-acid-green animate-pulse" data-id="element-727"></span>
                      <span className="font-barlow font-bold text-[10px] text-acid-green tracking-[3px] uppercase" data-id="element-728">
                        {current.tag}
                      </span>
                    </div>

                    {/* Icon */}
                    <div className="text-acid-green mb-6" data-id="element-729">
                      <current.Icon size={42} strokeWidth={1.5} data-id="element-730" />
                    </div>

                    {/* Title */}
                    <h3 className="font-bebas text-4xl md:text-5xl lg:text-6xl leading-[0.9] tracking-tight mb-6 text-white drop-shadow-lg" data-id="element-731">
                      {current.title}
                    </h3>

                    {/* Body */}
                    <p className="font-barlow text-lg text-white/90 leading-[1.7] max-w-xl text-justify hyphens-auto drop-shadow-md font-medium" data-id="element-732">
                      {current.body}
                    </p>

                    {/* Footer with progress */}
                    <div className="mt-auto pt-8 flex items-center justify-between" data-id="element-733">
                      <div className="font-barlow font-bold text-[10px] text-text-dimmed tracking-[3px] uppercase" data-id="element-734">
                        Mission {current.num} / 06
                      </div>
                      <div className="flex gap-1.5" data-id="element-735">
                        {cases.map((_, i) => <button key={i} onClick={() => setActive(i)} aria-label={`Go to mission ${i + 1}`} className={`h-[2px] transition-all duration-300 ${i === active ? 'w-8 bg-acid-green' : 'w-4 bg-dark-3 hover:bg-text-dimmed'}`} data-id="element-736" />)}
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
};